import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import {
  IPagedResponse,
  ISolicitacaoTipologiaDetalhe,
  ISolicitacaoTipologiaDetalheAnexo,
  ISolicitacaoTipologiaListaItem,
  SolicitacaoTipologiaAcao,
  SolicitacaoSistema,
} from "shared/interfaces/solicitacao";

export interface IListarSolicitacoesParams {
  empresaId?: number;
  page?: number;
  limit?: number;
  projetistaId?: number;
  projetistaIds?: string;
  linhaId?: number;
  situacoes?: number[];
  dataInicio?: string;
  dataFim?: string;
  apenasEmergenciais?: boolean;
  somenteVinculadas?: boolean;
  pesquisa?: string;
}

export const listarSolicitacoes = async (params: IListarSolicitacoesParams) => {
  try {
    const queryParams: Record<string, any> = {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
    };

    if (params.empresaId) {
      queryParams.empresaId = params.empresaId;
    }
    if (params.projetistaId) {
      queryParams.projetistaId = params.projetistaId;
    }
    if (params.projetistaIds) {
      queryParams.projetistaIds = params.projetistaIds;
    }
    if (params.linhaId) {
      queryParams.linhaId = params.linhaId;
    }
    if (params.situacoes && params.situacoes.length > 0) {
      queryParams.situacoes = params.situacoes.join(",");
    }
    if (params.dataInicio) {
      queryParams.dataInicio = params.dataInicio;
    }
    if (params.dataFim) {
      queryParams.dataFim = params.dataFim;
    }
    if (params.apenasEmergenciais) {
      queryParams.apenasEmergenciais = params.apenasEmergenciais;
    }
    if (params.somenteVinculadas) {
      queryParams.somenteVinculadas = params.somenteVinculadas;
    }
    if (params.pesquisa) {
      queryParams.busca = params.pesquisa;
    }

    const { data } = await api.get<IPagedResponse<ISolicitacaoTipologiaListaItem>>("/Solicitacao", {
      params: queryParams,
    });

    return data ?? {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar solicitações de tipologias.");
    return {
      items: [],
      page: 1,
      totalPages: 0,
      totalRecords: 0,
      paginate: true,
    } as IPagedResponse<ISolicitacaoTipologiaListaItem>;
  }
};

export interface IWeSignStartResponse {
  documentId: string;
  embedUrl: string;
}

export interface IWeSignStatusResponse {
  status: string | null;
}

export const iniciarAssinaturaWeSign = async (
  solicitacaoId: number,
  signerName: string,
  signerEmail: string,
  signerCpf: string,
  pdfFile: File,
) => {
  try {
    const formData = new FormData();
    formData.append("signerName", signerName);
    formData.append("signerEmail", signerEmail);
    formData.append("signerCpf", signerCpf);
    formData.append("pdfFile", pdfFile);

    const { data } = await api.post<IWeSignStartResponse>(
      `/Solicitacao/${solicitacaoId}/wesign/iniciar`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao iniciar assinatura eletrônica.");
    return null;
  }
};

export const obterStatusWeSign = async (documentId: string) => {
  try {
    const { data } = await api.get<IWeSignStatusResponse>(`/Solicitacao/wesign/${encodeURIComponent(documentId)}/status`);
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao consultar status da assinatura eletrônica.");
    return null;
  }
};

export const obterSolicitacao = async (solicitacaoId: number) => {
  try {
    const { data } = await api.get<ISolicitacaoTipologiaDetalhe>(`/Solicitacao/${solicitacaoId}`);
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao carregar detalhes da solicitação.");
    return null;
  }
};

export const atualizarSituacaoSolicitacao = async (
  solicitacaoId: number,
  acao: SolicitacaoTipologiaAcao,
  sistema?: SolicitacaoSistema
) => {
  try {
    // Montar o body apenas com os campos necessários
    // Se sistema for undefined, não incluir no body (backend aceita null/undefined)
    const body: { acao: SolicitacaoTipologiaAcao; sistema?: SolicitacaoSistema } = {
      acao,
    };
    
    if (sistema !== undefined) {
      body.sistema = sistema;
    }

    const { data } = await api.put<boolean>(`/Solicitacao/${solicitacaoId}/situacao`, body);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao atualizar situação da solicitação.");
    return false;
  }
};

export const atualizarEmergencialSolicitacao = async (
  solicitacaoId: number,
  emergencial: boolean
) => {
  try {
    const { data } = await api.put<boolean>(`/Solicitacao/${solicitacaoId}/emergencial`, {
      emergencial,
    });
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao atualizar status emergencial da solicitação.");
    return false;
  }
};

export const atualizarObservacaoSolicitacao = async (
  solicitacaoId: number,
  observacao: string
) => {
  try {
    const { data } = await api.put<boolean>(`/Solicitacao/${solicitacaoId}/observacao`, {
      observacao,
    });
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao atualizar observação da solicitação.");
    return false;
  }
};

export const adicionarAnexoSolicitacao = async (
  solicitacaoId: number,
  arquivo: File
) => {
  try {
    const formData = new FormData();
    formData.append("arquivo", arquivo);

    const { data } = await api.post<ISolicitacaoTipologiaDetalheAnexo>(
      `/Solicitacao/${solicitacaoId}/anexos`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao anexar arquivo na solicitação.");
    return null;
  }
};

export const marcarTipologiaComoReprovada = async (
  solicitacaoId: number,
  itemId: number,
  observacao?: string
) => {
  try {
    const { data } = await api.put<boolean>(
      `/Solicitacao/${solicitacaoId}/tipologias/${itemId}/reprovar`,
      { observacao }
    );

    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao reprovar tipologia da solicitação.");
    return false;
  }
};

export const baixarArquivoSolicitacao = async (solicitacaoId: number) => {
  try {
    const response = await api.get(`/Solicitacao/${solicitacaoId}/documento-pdf`, {
      responseType: "blob",
    });

    return response.data as Blob;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao baixar documento técnico.");
    return null;
  }
};

export const baixarAnexoSolicitacao = async (solicitacaoId: number, anexoId: number) => {
  try {
    const response = await api.get(`/Solicitacao/${solicitacaoId}/anexos/${anexoId}`, {
      responseType: "blob",
    });
    return response.data as Blob;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao baixar anexo da solicitação.");
    return null;
  }
};

export const baixarDocumentoTecnico = async (solicitacaoId: number) => {
  try {
    const response = await api.get(`/Solicitacao/${solicitacaoId}/documento-pdf`, {
      responseType: "blob",
    });
    return response.data as Blob;
  } catch (error: any) {
    getErrorsByApi(error, "Documento técnico não encontrado para esta solicitação.");
    return null;
  }
};

export const excluirSolicitacao = async (solicitacaoId: number) => {
  try {
    const { data } = await api.delete<boolean>(`/Solicitacao/${solicitacaoId}`);
    return data;
  } catch (error: any) {
    // getErrorsByApi lança um erro com a mensagem do backend
    // Deixamos o erro propagar para que seja tratado no componente com notificação
    getErrorsByApi(error, "Erro ao excluir solicitação.");
    // Este return nunca será executado porque getErrorsByApi lança um erro
    return false;
  }
};

export const removerTipologiaDaSolicitacao = async (
  solicitacaoId: number,
  itemId: number
) => {
  try {
    const { data } = await api.delete<boolean>(
      `/Solicitacao/${solicitacaoId}/tipologias/${itemId}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover tipologia da solicitação.");
    return false;
  }
};

export interface IUploadHtmlResponse {
  caminho: string;
  nomeArquivo: string;
  sistema: string;
  dataUpload: string;
}

export const uploadHtmlSolicitacao = async (
  solicitacaoId: number,
  arquivoHtml: File,
  sistema: SolicitacaoSistema
): Promise<IUploadHtmlResponse | null> => {
  try {
    const formData = new FormData();
    formData.append("arquivoHtml", arquivoHtml);
    formData.append("sistema", sistema.toString()); // Backend espera int, mas FormData converte automaticamente

    const { data } = await api.post<IUploadHtmlResponse>(
      `/Solicitacao/${solicitacaoId}/upload-html`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload do arquivo HTML.");
    return null;
  }
};

